import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { eventApi } from '../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../core/api/rtk-query/group.api';
import { getDayjsInGMT } from '../../../utils/dayjs.utils';
import { GrantAccessModal } from './GrantAccessModal';
import { EventResultsModal } from './EventResultsModal';
import { DeleteEventConfirm } from './DeleteEventConfirm';

const Wrap = styled.div`
  display: grid;
  gap: 16px;
  padding: 24px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dbe3f6;
  background: #fff;
  color: #0b1221;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #0b68fe;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dbe3f6;
  background: #fff;
  color: #0b1221;
  font-size: 14px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #0b68fe;
  }
`;

const Btn = styled.button`
  background: #fff;
  border: 1px solid #dbe3f6;
  color: #0b1221;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover {
    background: #f7f9ff;
    border-color: #0b68fe;
  }
  &:active {
    transform: scale(0.98);
  }
`;

const PrimaryBtn = styled(Btn)`
  background: linear-gradient(180deg, #0b68fe 0%, #0a5be3 100%);
  color: #fff;
  border: none;
  &:hover {
    background: linear-gradient(180deg, #0a5be3 0%, #0950c9 100%);
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e8f0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s;
  overflow: hidden;
  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f4ff;
  color: #0b68fe;
  border: 1px solid #d9e3ff;
  font-weight: 500;
  white-space: nowrap;
`;

const StatusTag = styled(Tag)<{ $active?: boolean }>`
  background: ${props => (props.$active ? '#e8f5e9' : '#fef3e8')};
  color: ${props => (props.$active ? '#2e7d32' : '#e65100')};
  border-color: ${props => (props.$active ? '#c8e6c9' : '#ffe0b2')};
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #0b1221;
`;

const Description = styled.div`
  color: #667085;
  font-size: 14px;
  line-height: 1.5;
`;

const InfoText = styled.div`
  color: #667085;
  font-size: 13px;
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #667085;
  font-size: 15px;
`;

const LoadingState = styled(EmptyState)`
  color: #0b68fe;
`;

export const TeacherEventsPage = () => {
  const navigate = useNavigate();
  // Используем getTeacherEvents - возвращает мероприятия где преподаватель является участником
  // Правильная логика: показываем только мероприятия к которым у преподавателя есть доступ
  const { data: events = [], isLoading, error } = eventApi.useGetTeacherEventsQuery();
  const { data: activeGroups } = groupApi.useGetActiveGroupQuery();
  const { data: archivedGroups } = groupApi.useGetArchivedGroupQuery();
  const allGroups = [...(activeGroups || []), ...(archivedGroups || [])];
  const [status, setStatus] = useState<'all' | 'active' | 'finished'>('all');
  const [type, setType] = useState<'all' | 'Homework' | 'Contest' | 'Milestone' | 'Exam'>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [grantEvent, setGrantEvent] = useState<string | undefined>();
  const [resultsEvent, setResultsEvent] = useState<string | undefined>();
  const [removeEvent, setRemoveEvent] = useState<string | undefined>();

  const filtered = useMemo(
    () =>
      (events || [])
        .filter(e => {
          const now = Date.now();
          const isActive = new Date(e.endTime).getTime() > now;
          if (status === 'active' && !isActive) return false;
          if (status === 'finished' && isActive) return false;
          if (type !== 'all' && e.type !== type) return false;
          if (groupFilter !== 'all' && e.groupId !== groupFilter) return false;
          if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false;
          return true;
        })
        .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()),
    [events, status, type, groupFilter, search],
  );

  const getTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      Homework: 'Домашка',
      Contest: 'Контест',
      Milestone: 'Мильстоун',
      Exam: 'Экзамен',
    };
    return labels[eventType] || eventType;
  };

  return (
    <Wrap>
      <Toolbar>
        <h2 style={{ margin: 0, fontSize: 24, color: '#0b1221' }}>Мероприятия</h2>
        <Select value={status} onChange={e => setStatus(e.target.value as any)}>
          <option value="all">Все</option>
          <option value="active">Активные</option>
          <option value="finished">Завершённые</option>
        </Select>
        <Select value={type} onChange={e => setType(e.target.value as any)}>
          <option value="all">Тип: Все</option>
          <option value="Homework">Домашка</option>
          <option value="Contest">Контест</option>
          <option value="Milestone">Мильстоун</option>
          <option value="Exam">Экзамен</option>
        </Select>
        <Select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
          <option value="all">Курс: Все</option>
          {allGroups.map(group => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Input placeholder="Поиск по названию..." value={search} onChange={e => setSearch(e.target.value)} />
          <PrimaryBtn onClick={() => navigate('/teacher/tasks/create')}>+ Создать мероприятие</PrimaryBtn>
        </div>
      </Toolbar>

      {error && (
        <div
          style={{
            background: '#fff0f0',
            border: '1px solid #ffd0d0',
            color: '#900',
            padding: 16,
            borderRadius: 12,
          }}
        >
          Ошибка загрузки мероприятий: {typeof error === 'object' ? JSON.stringify(error) : String(error)}
        </div>
      )}

      {isLoading && <LoadingState>Загрузка мероприятий...</LoadingState>}

      {!isLoading && !error && (
        <Grid>
          {filtered.map(e => {
            const from = getDayjsInGMT(e.startTime).format('DD.MM.YYYY, HH:mm');
            const to = getDayjsInGMT(e.endTime).format('DD.MM.YYYY, HH:mm');
            const isActive = new Date(e.endTime).getTime() > Date.now();

            return (
              <Card key={e.id}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Title style={{ cursor: 'pointer' }} onClick={() => navigate(`/teacher/tasks/edit/${e.id}`)}>
                    {e.name}
                  </Title>
                  <Row>
                    <StatusTag $active={isActive}>{isActive ? 'Активно' : 'Завершено'}</StatusTag>
                    <Tag>{getTypeLabel(e.type)}</Tag>
                  </Row>
                </div>

                {e.description && <Description>{e.description}</Description>}

                <InfoText>
                  📅 {from} — {to}
                </InfoText>
                <InfoText>
                  📝 Задач: {e.problems.length} • Мин. балл: {e.minimumPointsToPass}
                </InfoText>

                <Row style={{ gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                  <Btn onClick={() => navigate(`/teacher/tasks/edit/${e.id}`)}>✏️ Редактировать</Btn>
                  <Btn onClick={() => setGrantEvent(e.id)}>👥 Доступ</Btn>
                  <Btn onClick={() => setResultsEvent(e.id)}>📊 Результаты</Btn>
                  <Btn onClick={() => setRemoveEvent(e.id)} style={{ color: '#d32f2f' }}>
                    🗑️ Удалить
                  </Btn>
                </Row>
              </Card>
            );
          })}
          {filtered.length === 0 && !isLoading && !error && (
            <EmptyState>
              {search || status !== 'all' || type !== 'all'
                ? 'Мероприятий по заданным фильтрам не найдено'
                : 'У вас пока нет мероприятий. Создайте первое!'}
            </EmptyState>
          )}
        </Grid>
      )}

      {grantEvent && <GrantAccessModal eventId={grantEvent} onClose={() => setGrantEvent(undefined)} />}
      {resultsEvent && <EventResultsModal eventId={resultsEvent} onClose={() => setResultsEvent(undefined)} />}
      {removeEvent && <DeleteEventConfirm eventId={removeEvent} onClose={() => setRemoveEvent(undefined)} />}
    </Wrap>
  );
};
